import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/hooks/use-api'

// ---------------------------------------------------------------------------
// Staff Console -> Teams (Enterprise teams, 2026-10-03).
// Membership lives on the leads worker (/api/admin/orgs/*); seat billing on
// the webhooks worker (/api/billing/admin/*), which holds the Stripe key.
// ---------------------------------------------------------------------------

export type TeamOrg = {
  id: string
  name: string
  owner_id: string
  created_at: string
  member_count: number
  manager_count: number
  seat_plan: string | null
  seat_count: number | null
  billing_email: string | null
  stripe_subscription_id: string | null
  billing_status: string | null
}

export type TeamMember = {
  user_id: string
  role: 'owner' | 'admin' | 'manager' | 'member'
  joined_at: string
  email: string | null
  full_name: string | null
  plan: string | null
  plan_status: string | null
  is_comp: number | null
  stripe_subscription_id: string | null
}

export type SeatSync = { status: 'not_billed' | 'synced' | 'skipped' | 'failed'; detail?: string; seat_count?: number }

const KEY = ['admin', 'orgs'] as const

export function useTeamOrgs() {
  const api = useApi()
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api<{ orgs: TeamOrg[] }>('/api/admin/orgs')
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data.orgs
    },
  })
}

export function useTeamOrg(orgId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const res = await api<{ org: TeamOrg; members: TeamMember[] }>(`/api/admin/orgs/${encodeURIComponent(orgId ?? '')}`)
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed')
      return res.data
    },
  })
}

function useOrgMutation<V, R>(fn: (api: ReturnType<typeof useApi>, vars: V) => Promise<R>) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: V) => fn(api, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
    },
  })
}

function unwrap<T>(res: { success: boolean; data?: T; error?: string }): T {
  if (!res.success || res.data === undefined) throw new Error(res.error ?? 'Failed')
  return res.data
}

export function useCreateTeamOrg() {
  return useOrgMutation(async (api, vars: { name: string }) =>
    unwrap(await api<{ org: { id: string; name: string } }>('/api/admin/orgs', { method: 'POST', body: vars })),
  )
}

export function useAddTeamMember() {
  return useOrgMutation(async (api, vars: { orgId: string; userIdOrEmail: string; role: 'manager' | 'member' }) => {
    const target = vars.userIdOrEmail.trim()
    const body = target.includes('@') ? { email: target, role: vars.role } : { user_id: target, role: vars.role }
    return unwrap(
      await api<{ moved_from: string[]; claims: { claimed: number; conflicts: number }; seats: SeatSync }>(
        `/api/admin/orgs/${encodeURIComponent(vars.orgId)}/members`,
        { method: 'POST', body },
      ),
    )
  })
}

export function useSetTeamRole() {
  return useOrgMutation(async (api, vars: { orgId: string; userId: string; role: 'manager' | 'member' }) =>
    unwrap(
      await api(`/api/admin/orgs/${encodeURIComponent(vars.orgId)}/members/${encodeURIComponent(vars.userId)}`, {
        method: 'PATCH',
        body: { role: vars.role },
      }),
    ),
  )
}

export function useRemoveTeamMember() {
  return useOrgMutation(async (api, vars: { orgId: string; userId: string }) =>
    unwrap(
      await api<{ seat_ended: { ended: boolean } | null; seats: SeatSync }>(
        `/api/admin/orgs/${encodeURIComponent(vars.orgId)}/members/${encodeURIComponent(vars.userId)}`,
        { method: 'DELETE' },
      ),
    ),
  )
}

export function useOrgSeatCheckout() {
  return useOrgMutation(async (api, vars: { orgId: string; plan: 'starter' | 'pro'; billingEmail: string; seats?: number; seatPriceUsd?: number }) =>
    unwrap(
      await api<{ url: string; seats: number }>('/api/billing/admin/org-checkout', {
        method: 'POST',
        body: { org_id: vars.orgId, plan: vars.plan, billing_email: vars.billingEmail, seats: vars.seats, seat_price_usd: vars.seatPriceUsd },
      }),
    ),
  )
}

export function useSyncOrgSeats() {
  return useOrgMutation(async (api, vars: { orgId: string }) =>
    unwrap(
      await api<{ seat_count: number; changed: boolean }>(
        `/api/billing/admin/orgs/${encodeURIComponent(vars.orgId)}/seats/sync`,
        { method: 'POST' },
      ),
    ),
  )
}
